import React, {
  useCallback,
  useState,
  createContext,
  useContext,
  useRef,
  useEffect,
  useMemo,
} from "react";
import {
  DndContext,
  rectIntersection,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";

const TrackDndContext = createContext({ activeItem: null });

/**
 * Hook to access active drag item info from children
 */
export const useTrackDnd = () => useContext(TrackDndContext);

/**
 * Shared DND Provider for track management
 * Handles sensors, context, and overlay boilerplate
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Components inside the DND context
 * @param {Function} props.onDragStart - Callback when drag starts
 * @param {Function} props.onDragOver - Callback when item is dragged over another
 * @param {Function} props.onDragEnd - Callback when drag ends
 * @param {Function} props.renderOverlay - Function to render the drag overlay item
 * @param {Object} props.processedCarsMap - Map of tracks to railcars (to find active item)
 */
export const TrackDndProvider = ({
  children,
  onDragStart,
  onDragOver,
  onDragEnd,
  renderOverlay,
  processedCarsMap,
  disabled = false,
}) => {
  const [activeItem, setActiveItem] = useState(null);
  const overlayRafRef = useRef(null);

  const scheduleOverlayUpdate = useCallback((nextItem) => {
    if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
      setActiveItem(nextItem);
      return;
    }

    if (overlayRafRef.current) {
      cancelAnimationFrame(overlayRafRef.current);
    }

    overlayRafRef.current = requestAnimationFrame(() => {
      setActiveItem(nextItem);
    });
  }, []);

  useEffect(
    () => () => {
      if (overlayRafRef.current) {
        cancelAnimationFrame(overlayRafRef.current);
      }
    },
    []
  );

  // Unify sensors: Pointer for general use, Touch with delay for mobile "long press" feel
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300, // ELITE UX: Increased delay to allow long-press selection before drag starts
        tolerance: 8, // Increased tolerance for shaky fingers on mobile
      },
    })
  );

  const handleDragStart = useCallback(
    (event) => {
      const { active } = event;
      let item = null;
      
      if (processedCarsMap) {
        for (const cars of Object.values(processedCarsMap)) {
          const found = cars.find((c) => c.id === active.id);
          if (found) {
            item = found;
            break;
          }
        }
      }
      
      scheduleOverlayUpdate(item);
      if (onDragStart) onDragStart(event);
    },
    [onDragStart, processedCarsMap, scheduleOverlayUpdate]
  );

  const handleDragOver = useCallback(
    (event) => {
      if (onDragOver) onDragOver(event);
    },
    [onDragOver]
  );

  const handleDragEnd = useCallback(
    (event) => {
      scheduleOverlayUpdate(null);
      if (onDragEnd) onDragEnd(event);
    },
    [onDragEnd, scheduleOverlayUpdate]
  );

  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: "0.3",
        },
      },
    }),
  };

  const contextValue = useMemo(() => ({ activeItem }), [activeItem]);

  return (
    <DndContext
      sensors={disabled ? [] : sensors}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <TrackDndContext.Provider value={contextValue}>
        {children}
      </TrackDndContext.Provider>

      <DragOverlay dropAnimation={dropAnimation}>
        {activeItem && renderOverlay ? renderOverlay(activeItem) : null}
      </DragOverlay>
    </DndContext>
  );
};

